"use client";

import { useCallback, useEffect, useRef } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { requestJson } from "@/lib/clientRequest";
import { logDataDiag } from "@/lib/dataDiagnostics";
import {
  getPwaReloadSafety,
  PWA_RELOAD_SAFETY_EVENT,
} from "@/lib/pwaReloadSafety";
import {
  PWA_STARTUP_STATE_EVENT,
  PWA_WORKER_CONTRACT_VERSION,
  getPwaWorkerUrl,
  isExpectedPwaWorkerScript,
} from "@/lib/pwaRuntimeContract";

const ENABLE_DEV_SERVICE_WORKER = process.env.NEXT_PUBLIC_ENABLE_PWA_SW_DEV === "1";
const AUTH_DIAGNOSTICS_ENABLED = process.env.NEXT_PUBLIC_AUTH_DIAG === "1";
const VERSION_CHECK_THROTTLE_MS = 60_000;

function isSameOriginRegistration(registration) {
  if (typeof window === "undefined" || !registration?.scope) return false;
  try {
    return new URL(registration.scope).origin === window.location.origin;
  } catch {
    return false;
  }
}

function workerVersion(worker) {
  try {
    return new URL(worker?.scriptURL || "", window.location.origin).searchParams.get("v");
  } catch {
    return null;
  }
}

function isExpectedWorker(worker, currentVersion) {
  return isExpectedPwaWorkerScript(
    worker?.scriptURL,
    currentVersion,
    window.location.origin
  );
}

function requestVerifiedActivation(worker, currentVersion, trigger) {
  if (!worker || !isExpectedWorker(worker, currentVersion)) return false;
  const startupRuntime = window.__ybPwaStartup;
  if (
    startupRuntime?.contractVersion === PWA_WORKER_CONTRACT_VERSION &&
    typeof startupRuntime.requestActivation === "function"
  ) {
    return startupRuntime.requestActivation(worker, {
      force: true,
      trigger,
    });
  }
  try {
    worker.postMessage({
      type: "SKIP_WAITING",
      contract: PWA_WORKER_CONTRACT_VERSION,
    });
    return true;
  } catch {
    return false;
  }
}

async function cleanupDevelopmentServiceWorkers() {
  if (!("serviceWorker" in navigator)) return;

  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(
      registrations
        .filter(isSameOriginRegistration)
        .map((registration) => registration.unregister())
    );
  } catch (error) {
    console.warn("[PWA] Failed to unregister development service worker", error);
  }

  if (!("caches" in window)) return;
  try {
    const cacheNames = await window.caches.keys();
    await Promise.all(
      cacheNames
        .filter((cacheName) => cacheName.startsWith("yb-pwa-"))
        .map((cacheName) => window.caches.delete(cacheName))
    );
  } catch (error) {
    console.warn("[PWA] Failed to clear development service worker caches", error);
  }
}

export default function PwaServiceWorkerRegistration({ currentVersion = "development" }) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const searchSignature = searchParams?.toString() || "";
  const waitingWorkerRef = useRef(null);
  const lastVersionCheckRef = useRef(0);
  const activationAttemptedWorkerRef = useRef(null);
  const activateWaitingWorkerRef = useRef(() => false);

  const refreshReloadSafety = useCallback(() => {
    const safety = getPwaReloadSafety();
    if (safety.safe) activateWaitingWorkerRef.current();
  }, []);

  useEffect(() => {
    window.addEventListener(PWA_RELOAD_SAFETY_EVENT, refreshReloadSafety);
    window.addEventListener("online", refreshReloadSafety);
    window.addEventListener("offline", refreshReloadSafety);
    return () => {
      window.removeEventListener(PWA_RELOAD_SAFETY_EVENT, refreshReloadSafety);
      window.removeEventListener("online", refreshReloadSafety);
      window.removeEventListener("offline", refreshReloadSafety);
    };
  }, [refreshReloadSafety]);

  useEffect(() => {
    refreshReloadSafety();
    window.__ybPwaStartup?.refreshRouteState?.("react_route_change");
  }, [pathname, refreshReloadSafety, searchSignature]);

  useEffect(() => {
    const isProduction = process.env.NODE_ENV === "production";
    if (!isProduction && !ENABLE_DEV_SERVICE_WORKER) {
      void cleanupDevelopmentServiceWorkers();
      return undefined;
    }
    if (!("serviceWorker" in navigator)) return undefined;

    let isMounted = true;
    let registration = null;
    const cleanupHandlers = [];

    const activateWaitingWorkerWhenSafe = (candidate = waitingWorkerRef.current) => {
      if (!candidate || !getPwaReloadSafety().safe) return false;
      if (!isExpectedWorker(candidate, currentVersion)) {
        void registration?.update?.().catch(() => {});
        logDataDiag("service_worker:unverified_waiting_worker", {
          currentVersion: String(currentVersion),
          waitingVersion: workerVersion(candidate),
        });
        return false;
      }
      if (activationAttemptedWorkerRef.current === candidate) return true;
      activationAttemptedWorkerRef.current = candidate;
      if (
        requestVerifiedActivation(
          candidate,
          currentVersion,
          "react_safe_auto_activation"
        )
      ) {
        logDataDiag("service_worker:safe_auto_activation", {
          currentVersion: String(currentVersion),
          waitingVersion: workerVersion(candidate),
        });
        return true;
      }
      return false;
    };
    activateWaitingWorkerRef.current = activateWaitingWorkerWhenSafe;

    const handleUpdateAvailable = (worker = null) => {
      if (!isMounted) return;
      if (worker && isExpectedWorker(worker, currentVersion)) {
        waitingWorkerRef.current = worker;
      } else if (worker) {
        waitingWorkerRef.current = null;
        void registration?.update?.().catch(() => {});
      }
      activateWaitingWorkerWhenSafe(worker || waitingWorkerRef.current);
      logDataDiag("service_worker:update_available", {
        currentVersion: String(currentVersion),
        waitingVersion: workerVersion(worker),
      });
    };

    const checkForVersion = async ({ force = false } = {}) => {
      if (!isMounted || document.visibilityState === "hidden") return;
      const now = Date.now();
      if (!force && now - lastVersionCheckRef.current < VERSION_CHECK_THROTTLE_MS) return;
      lastVersionCheckRef.current = now;
      try {
        const { payload } = await requestJson("/api/app-version", {
          cache: "no-store",
          timeoutMs: 4000,
          retries: 0,
        });
        if (!isMounted) return;
        const deployedVersion = String(payload?.version || "");
        if (deployedVersion && deployedVersion !== String(currentVersion)) {
          logDataDiag("service_worker:version_mismatch", {
            currentVersion: String(currentVersion),
            deployedVersion,
          });
          handleUpdateAvailable(registration?.waiting || null);
          void registration?.update?.().catch(() => {});
        }
      } catch {
        // Network recovery UI owns offline feedback; version checks are passive.
      }
    };

    const handleControllerChange = () => {
      if (!isMounted) return;
      const activeVersion = workerVersion(navigator.serviceWorker.controller);
      logDataDiag("service_worker:controller_change", {
        activeVersion,
        currentVersion: String(currentVersion),
      });
      waitingWorkerRef.current = null;
      activationAttemptedWorkerRef.current = null;
      if (activeVersion && activeVersion !== String(currentVersion)) {
        handleUpdateAvailable();
      }
    };
    navigator.serviceWorker.addEventListener("controllerchange", handleControllerChange);

    const registerServiceWorker = () => {
      const startupRuntime = window.__ybPwaStartup;
      const existingRegistrationPromise =
        startupRuntime?.contractVersion === PWA_WORKER_CONTRACT_VERSION &&
        startupRuntime?.expectedWorkerVersion === String(currentVersion)
          ? startupRuntime.registrationPromise
          : null;
      const nextRegistrationPromise =
        existingRegistrationPromise ||
        navigator.serviceWorker.register(
          getPwaWorkerUrl(currentVersion, {
            authDiagnostics: AUTH_DIAGNOSTICS_ENABLED,
          }),
          {
            scope: "/",
            updateViaCache: "none",
          }
        );

      Promise.resolve(nextRegistrationPromise)
        .then((nextRegistration) => {
          if (!isMounted) return;
          registration = nextRegistration;
          logDataDiag("service_worker:registered", {
            currentVersion: String(currentVersion),
            scope: nextRegistration.scope,
          });
          if (nextRegistration.waiting && navigator.serviceWorker.controller) {
            handleUpdateAvailable(nextRegistration.waiting);
          }

          const handleUpdateFound = () => {
            const installingWorker = nextRegistration.installing;
            if (!installingWorker) return;
            const handleStateChange = () => {
              if (installingWorker.state === "installed" && navigator.serviceWorker.controller) {
                handleUpdateAvailable(installingWorker);
              }
            };
            installingWorker.addEventListener("statechange", handleStateChange);
            cleanupHandlers.push(() => installingWorker.removeEventListener("statechange", handleStateChange));
          };
          nextRegistration.addEventListener("updatefound", handleUpdateFound);
          cleanupHandlers.push(() => nextRegistration.removeEventListener("updatefound", handleUpdateFound));
          void checkForVersion({ force: true });
        })
        .catch((error) => {
          if (!isProduction) console.warn("[PWA] Service worker registration failed", error);
        });
    };

    const checkOnResume = () => {
      if (document.visibilityState === "visible") void checkForVersion();
    };
    const checkOnOnline = () => void checkForVersion({ force: true });
    const checkOnPageShow = (event) => {
      if (event?.persisted) {
        void window.__ybPwaStartup?.checkWorker?.("react_pageshow_persisted", true);
        void checkForVersion({ force: true });
      } else {
        void checkForVersion();
      }
    };
    const handleStartupState = () => {
      const startupRuntime = window.__ybPwaStartup;
      const startupRegistration = startupRuntime?.registration;
      if (startupRegistration) {
        registration = startupRegistration;
      }
      const waitingWorker = startupRegistration?.waiting || null;
      if (waitingWorker && isExpectedWorker(waitingWorker, currentVersion)) {
        waitingWorkerRef.current = waitingWorker;
      }
    };
    window.addEventListener("focus", checkOnResume);
    window.addEventListener("online", checkOnOnline);
    window.addEventListener("pageshow", checkOnPageShow);
    window.addEventListener(PWA_STARTUP_STATE_EVENT, handleStartupState);
    document.addEventListener("visibilitychange", checkOnResume);
    handleStartupState();

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", registerServiceWorker, { once: true });
    } else {
      registerServiceWorker();
    }

    return () => {
      isMounted = false;
      activateWaitingWorkerRef.current = () => false;
      navigator.serviceWorker.removeEventListener("controllerchange", handleControllerChange);
      document.removeEventListener("DOMContentLoaded", registerServiceWorker);
      window.removeEventListener("focus", checkOnResume);
      window.removeEventListener("online", checkOnOnline);
      window.removeEventListener("pageshow", checkOnPageShow);
      window.removeEventListener(PWA_STARTUP_STATE_EVENT, handleStartupState);
      document.removeEventListener("visibilitychange", checkOnResume);
      cleanupHandlers.forEach((cleanup) => cleanup());
    };
  }, [currentVersion]);
  return null;
}
